import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/site/Navbar";
import { Continuation, Footer } from "@/components/site/Continuation";
import { ContactDialog } from "@/components/site/ContactDialog";
import { Reveal, EASE } from "@/components/site/Reveal";
import { RESOURCES, RESOURCE_CATEGORIES } from "@/data/resources";
import { ArrowUpRight, FileText, Play } from "lucide-react";

const ResourceCard = ({ r, onOpen, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: index * 0.06, ease: EASE }}
    className="group flex flex-col rounded-2xl bg-white border border-[#3E2818]/10 overflow-hidden hover:border-[#D1633D]/40 hover:shadow-[0_24px_60px_-34px_rgba(33,19,7,0.35)] transition-all duration-300"
    data-testid={`resource-card-${r.id}`}
  >
    <button
      onClick={() => onOpen(r)}
      className="relative aspect-[16/9] overflow-hidden bg-[#1E2340] text-left"
      data-testid={`resource-cover-${r.id}`}
    >
      <img src={r.cover} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
      {r.type === "video" && (
        <span className="absolute inset-0 flex items-center justify-center" data-testid={`resource-play-${r.id}`}>
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#211307]/55 backdrop-blur-sm ring-1 ring-white/30 transition-transform duration-300 group-hover:scale-110">
            <Play size={22} className="text-white translate-x-[1px]" fill="currentColor" />
          </span>
        </span>
      )}
      <span className="absolute top-3 left-3 rounded-full bg-[#211307]/85 backdrop-blur px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#E8AA68]">
        {r.category}
      </span>
    </button>
    <div className="flex flex-col flex-1 p-5">
      <div className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#3E2818]/40">
        {r.type === "video" ? <Play size={12} /> : <FileText size={12} />} <span>{r.meta}</span><span>·</span><span>{r.date}</span>
      </div>
      <h3 className="mt-2 font-display text-lg leading-snug text-[#211307]">{r.title}</h3>
      <p className="mt-2 text-[13px] text-[#3E2818]/60 leading-relaxed flex-1">{r.description}</p>
      <button
        onClick={() => onOpen(r)}
        data-testid={`resource-open-${r.id}`}
        className="mt-4 inline-flex items-center gap-1.5 self-start font-medium text-[13px] text-[#D1633D] hover:gap-2.5 transition-all"
      >
        {r.cta || "Read the story"} <ArrowUpRight size={15} />
      </button>
    </div>
  </motion.article>
);

export default function Resources() {
  const [contactSource, setContactSource] = useState(null);
  const [active, setActive] = useState("All");

  const openResource = (r) => window.open(r.file, "_blank", "noopener,noreferrer");

  const categories = useMemo(
    () => ["All", ...RESOURCE_CATEGORIES.slice(1).filter((c) => RESOURCES.some((r) => r.category === c))],
    []
  );
  const filtered = active === "All" ? RESOURCES : RESOURCES.filter((r) => r.category === active);

  return (
    <div className="bg-[#F7F5F0] min-h-screen text-left">
      <div className="grain" />
      <Navbar onContact={setContactSource} />

      {/* Header */}
      <header className="relative bg-[#211307] pt-32 pb-16 lg:pt-36 lg:pb-20 overflow-hidden" data-testid="resources-header">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#E0902B]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#E8AA68] mb-4">Resources & Knowledge</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-display font-medium tracking-tight text-[#F7F5F0] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              Ideas that turn AI ambition into <span className="text-[#D1633D]">AI outcomes</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 text-base text-[#F7F5F0]/65 max-w-xl leading-relaxed">
              Whitepapers, case studies and field notes on building agentic AI that enterprises actually adopt and trust.
            </p>
          </Reveal>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-14 lg:py-16">
        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 mb-10" data-testid="resource-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`resource-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-[13px] font-medium transition-colors ${
                active === c
                  ? "bg-[#211307] text-[#F7F5F0]"
                  : "bg-white border border-[#3E2818]/12 text-[#3E2818]/70 hover:border-[#D1633D]/50 hover:text-[#3E2818]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r, i) => (
              <ResourceCard key={r.id} r={r} index={i} onOpen={openResource} />
            ))}
          </div>
        ) : (
          <p className="text-[#3E2818]/50 py-16 text-center">More {active} content coming soon.</p>
        )}
      </main>

      <Continuation onContact={setContactSource} />
      <Footer onContact={setContactSource} />

      <ContactDialog source={contactSource} onClose={() => setContactSource(null)} />
    </div>
  );
}
